// Layer 3 — de-identified audit payload builder (client-side, pure).
//
// Assembles the evidence bundle the AI auditor sees. De-identification is BY
// EXPLICIT CONSTRUCTION, not by hoping the model ignores PII:
//   • Preamble rows (everything above the detected header — where customer
//     name / address / account live) are never passed in: the caller supplies
//     `dataRows` already sliced to start AT/AFTER the header row.
//   • Every column the profiler classified `id-like` (meter IDs, account
//     numbers, service-point IDs) is dropped from the header, the sample rows,
//     and the per-column stats. Columns whose kind is `numeric-large` or whose
//     stats show `isConstant` are dropped too — purely numeric account/meter
//     numbers the id-like classifier misses.
//   • Sheet TAB NAMES are replaced with positional labels ("Sheet 1", …) —
//     contractors name tabs after customers. Only dimensions survive. The
//     same labels are substituted wherever a plausibility flag embeds a tab
//     name (`rival_sheets` quotes the chosen tab and lists its competitors).
//   • The FILE NAME is never a parameter here (contractors name files after
//     service addresses), and no project/user identifiers are accepted.
//
// The output contains only: utility name, the (id-stripped) header, up to 50
// sample rows, per-column stats, a sheet inventory (generic labels +
// dimensions only — no row content from unanalyzed sheets), the parser's
// conclusion, and the Layer 2 plausibility flags (tab names scrubbed).

import type { ColumnKind, ColumnStats } from './types';
import type { PlausibilityFlag } from './plausibilityValidator';

export interface AuditColumn {
  index: number;
  header: string | null;
  kind: ColumnKind;
  stats: ColumnStats;
}

export interface AuditConclusion {
  chosenSheet: string | null;
  headerRowIndex: number | null;
  timestampColumn: string | null;
  demandColumn: string | null;
  unit: string | null;
  intervalMinutes: number | null;
  rowCount: number | null;
  spanDays: number | null;
  peakKw: number | null;
  peakTimestamp: string | null;
}

export interface AuditSheet {
  name: string;
  rowCount: number;
  colCount: number;
}

/** Everything the parser exposes for auditing. Assembled in parser.ts. */
export interface AuditArtifacts {
  headerRow: (string | null)[] | null;
  columns: AuditColumn[];
  idLikeIndices: number[];
  dataRows: unknown[][]; // rows AT/AFTER the detected header — never the preamble
  demandIndex: number;
  sheetInventory: AuditSheet[];
  conclusion: AuditConclusion;
  plausibilityFlags: PlausibilityFlag[];
}

/** The de-identified object transmitted to the auditor. */
export interface AuditPayload {
  utilityName: string | null;
  header: (string | null)[];
  sampleRows: unknown[][];
  columnStats: Array<{ header: string | null; kind: ColumnKind; stats: ColumnStats }>;
  sheetInventory: AuditSheet[];
  conclusion: AuditConclusion;
  plausibilityFlags: PlausibilityFlag[];
}

const MAX_SAMPLE_ROWS = 50;

function toNumber(raw: unknown): number {
  if (typeof raw === 'number') return raw;
  return parseFloat(String(raw ?? '').replace(/,/g, ''));
}

/**
 * Choose up to 50 representative data-row indices: the 10 centered on the peak
 * interval, the first 10 rows, and the remainder spread evenly across the file.
 */
function chooseSampleIndices(rowCount: number, peakIdx: number): number[] {
  if (rowCount <= 0) return [];
  const picked = new Set<number>();
  const add = (i: number) => {
    if (i >= 0 && i < rowCount) picked.add(i);
  };

  // First 10 rows (verify header/structure).
  for (let i = 0; i < 10; i++) add(i);
  // 10 centered on the detected peak (verify the peak reading).
  if (peakIdx >= 0) for (let i = peakIdx - 5; i < peakIdx + 5; i++) add(i);

  // Remainder: evenly spaced across the dataset until we reach the cap.
  const target = Math.min(MAX_SAMPLE_ROWS, rowCount);
  const step = Math.max(1, Math.floor(rowCount / MAX_SAMPLE_ROWS));
  for (let i = 0; i < rowCount && picked.size < target; i += step) add(i);
  // If a coarse step under-filled, top up sequentially.
  for (let i = 0; i < rowCount && picked.size < target; i++) add(i);

  return [...picked].sort((a, b) => a - b).slice(0, MAX_SAMPLE_ROWS);
}

/**
 * Replace raw sheet tab names with their positional labels inside plausibility
 * flags — the message text and every string in `detail`, however nested. Names
 * are matched longest-first in one alternation pass, so overlapping tab names
 * resolve to the longest match and an introduced label is never itself
 * re-scanned. A tab name that happens to collide with ordinary message text
 * gets replaced there too — a cosmetic cost accepted over ever leaking a name.
 *
 * Never mutates its input: the parser hands the same flag objects to the
 * in-app UI, where the real tab name is the useful one.
 */
function scrubSheetNames(
  flags: PlausibilityFlag[],
  labelByName: Map<string, string>,
): PlausibilityFlag[] {
  if (labelByName.size === 0) return flags;

  const pattern = new RegExp(
    [...labelByName.keys()]
      .sort((a, b) => b.length - a.length)
      .map(name => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .join('|'),
    'g',
  );
  const scrubString = (s: string): string => s.replace(pattern, m => labelByName.get(m) ?? m);
  const scrubValue = (v: unknown): unknown => {
    if (typeof v === 'string') return scrubString(v);
    if (Array.isArray(v)) return v.map(scrubValue);
    if (v !== null && typeof v === 'object') {
      return Object.fromEntries(
        Object.entries(v as Record<string, unknown>).map(([k, val]) => [k, scrubValue(val)]),
      );
    }
    return v;
  };

  return flags.map(f => ({
    ...f,
    message: scrubString(f.message),
    detail: scrubValue(f.detail) as Record<string, unknown>,
  }));
}

/**
 * Build the de-identified audit payload. Pure — no I/O, no globals. The only
 * caller-supplied identity value is `utilityName` (a public utility, not PII).
 */
export function buildAuditPayload(
  artifacts: AuditArtifacts,
  utilityName: string | null,
): AuditPayload {
  const idSet = new Set(artifacts.idLikeIndices);

  // Kept columns, in original order, minus id-like columns and minus
  // numeric-large / constant columns (purely numeric account and meter
  // numbers that the id-like classifier misses).
  const keptColumns = artifacts.columns.filter(
    c => !idSet.has(c.index) && c.kind !== 'numeric-large' && !c.stats.isConstant,
  );
  const keptIndices = keptColumns.map(c => c.index);

  const header = keptIndices.map(i => artifacts.headerRow?.[i] ?? null);

  const columnStats = keptColumns.map(c => ({
    header: c.header,
    kind: c.kind,
    stats: c.stats,
  }));

  // Locate the peak row by the (original-index) demand column, then sample.
  const rows = artifacts.dataRows;
  let peakIdx = -1;
  let peakVal = -Infinity;
  for (let i = 0; i < rows.length; i++) {
    const v = toNumber((rows[i] as unknown[])[artifacts.demandIndex]);
    if (Number.isFinite(v) && v > peakVal) {
      peakVal = v;
      peakIdx = i;
    }
  }

  const sampleRows = chooseSampleIndices(rows.length, peakIdx).map(i =>
    keptIndices.map(colIdx => (rows[i] as unknown[])[colIdx]),
  );

  // Tab names can contain customer names — transmit positional labels only.
  // A raw name is never forwarded, even when the chosen sheet is missing
  // from the inventory.
  const chosenSheetIdx = artifacts.sheetInventory.findIndex(
    s => s.name === artifacts.conclusion.chosenSheet,
  );
  const sheetLabel = (i: number) =>
    i === chosenSheetIdx ? `Sheet ${i + 1} (analyzed)` : `Sheet ${i + 1}`;
  const sheetInventory = artifacts.sheetInventory.map((s, i) => ({
    ...s,
    name: sheetLabel(i),
  }));
  const conclusion = {
    ...artifacts.conclusion,
    chosenSheet: chosenSheetIdx >= 0 ? sheetLabel(chosenSheetIdx) : null,
  };

  // Plausibility flags embed tab names too (`rival_sheets` quotes the chosen
  // tab and lists its competitors). Scrub them with the same positional
  // labels so the flags stay cross-referenceable against the inventory.
  const labelBySheetName = new Map<string, string>();
  artifacts.sheetInventory.forEach((s, i) => {
    if (s.name && !labelBySheetName.has(s.name)) labelBySheetName.set(s.name, sheetLabel(i));
  });

  return {
    utilityName,
    header,
    sampleRows,
    columnStats,
    sheetInventory,
    conclusion,
    plausibilityFlags: scrubSheetNames(artifacts.plausibilityFlags, labelBySheetName),
  };
}
